import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { SnackbarProvider } from "notistack";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Box } from "@mui/material";
import { colors, routes, token_keys } from "./constants";
import api from "../src/api";
import { refreshAccessToken } from "./api/axiosInstance";
import useStore from "./store";
import Loading from "./components/Loading";
import Layout from "./components/Layout";
import { useSessionTimeout } from "./hooks/useSessionTimeout";

// Public Pages
import Login from "./pages/Public/Login";
import Register from "./pages/Public/Register";
import ForgotPasswordPage from "./pages/Public/ForgotPasswordPage";
import ResetPasswordPage from "./pages/Public/ResetPasswordPage";
import ActivatePage from "./pages/Public/ActivatePage";
import RestoreAccountPage from "./pages/Public/RestoreAccountPage";
import SsoCallback from "./pages/Public/SsoCallback";
import RespondSurveyPage from "./pages/Survey/RespondSurveyPage";

// Authenticated Pages
// import HomePage from "./pages/Home/HomePage";
import HomePage from "./pages/Home/HomePageV2";
import { SurveyList } from "./pages/Survey/SurveyList";
import SurveyCreate from "./pages/Survey/SurveyCreate";
import SurveyEdit from "./pages/Survey/SurveyEdit";
import CollectList from "./pages/Collector/CollectorList";
import CollectorConfigure from "./pages/Collector/CollectorConfigure";
import InsightPage from "./pages/Insight/InsightPage";
import InsightDetailsPage from "./pages/Insight/InsightDetailsPage";
import HelpCenter from "./pages/Help/HelpCenter";
import DocumentsPage from "./pages/Documents/DocumentsPage";
import ProjectsPage from "./pages/Projects/ProjectsPage";
import CompareResultsPage from "./pages/Projects/CompareResultsPage";
import LegacyWorkspaceRedirect from "./pages/impacts/LegacyWorkspaceRedirect";
import ConverseLinkPage from "./pages/Survey/ConverseLinkPage";
import AccountDetailsPage from "./pages/Account/AccountDetailsPage";
import SubscriptionPage from "./pages/Account/SubscriptionPage";
import ManageUsersPage from "./pages/Account/ManageUsersPage";
import NotFound from "./pages/NotFound";
import LandingPage from "./pages/Public/LandingPage";


const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colors.main,
    },
    secondary: {
      main: colors.secondary,
    },
    background: {
      default: '#f7f9fc',
      paper: '#ffffff',
    },
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    // Base body font - Inter for clean, professional readability
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'Helvetica Neue',
      'Arial',
      'sans-serif',
    ].join(','),
    // Headings - Poppins for modern, bold impact
    h1: {
      fontFamily: ['Poppins', 'Inter', 'Roboto', 'sans-serif'].join(','),
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.2
    },
    h2: {
      fontFamily: ['Poppins', 'Inter', 'Roboto', 'sans-serif'].join(','),
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1.3
    },
    h3: {
      fontFamily: ['Poppins', 'Inter', 'Roboto', 'sans-serif'].join(','),
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1.3
    },
    h4: {
      fontFamily: ['Poppins', 'Inter', 'Roboto', 'sans-serif'].join(','),
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.4
    },
    h5: {
      fontFamily: ['Poppins', 'Inter', 'Roboto', 'sans-serif'].join(','),
      fontWeight: 600,
      letterSpacing: '-0.005em',
      lineHeight: 1.4
    },
    h6: {
      fontFamily: ['Poppins', 'Inter', 'Roboto', 'sans-serif'].join(','),
      fontWeight: 600,
      letterSpacing: '-0.005em',
      lineHeight: 1.4
    },
    // Body text - Inter for optimal readability
    body1: {
      fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
      fontWeight: 400,
      lineHeight: 1.6
    },
    body2: {
      fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
      fontWeight: 400,
      lineHeight: 1.5,
      fontSize: '0.875rem'
    },
    // Buttons - Montserrat for clear, action-oriented text
    button: {
      fontFamily: ['Montserrat', 'Poppins', 'Inter', 'Roboto', 'sans-serif'].join(','),
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.01em'
    },
    // Captions - Inter for small, readable text
    caption: {
      fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.4
    },
    // Overline - Work Sans for distinctive labels
    overline: {
      fontFamily: ['Work Sans', 'Inter', 'Roboto', 'sans-serif'].join(','),
      fontWeight: 600,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      fontSize: '0.75rem'
    },
    // Subtitle - Inter with slightly heavier weight
    subtitle1: {
      fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
      fontWeight: 500,
      lineHeight: 1.5,
      fontSize: '1rem'
    },
    subtitle2: {
      fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
      fontWeight: 500,
      lineHeight: 1.43,
      fontSize: '0.875rem'
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontFamily: ['Montserrat', 'Poppins', 'Inter', 'Roboto', 'sans-serif'].join(','),
          fontWeight: 600,
          letterSpacing: '0.01em',
          textTransform: 'none',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          // Ensure all Typography components have explicit font families
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        label: {
          fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
          fontWeight: 500,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
        },
        secondary: {
          fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: ['Poppins', 'Inter', 'Roboto', 'sans-serif'].join(','),
          fontWeight: 600,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        title: {
          fontFamily: ['Poppins', 'Inter', 'Roboto', 'sans-serif'].join(','),
          fontWeight: 600,
        },
        subheader: {
          fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: ['Montserrat', 'Inter', 'Roboto', 'sans-serif'].join(','),
          fontWeight: 600,
          textTransform: 'none',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        message: {
          fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
        },
        title: {
          fontFamily: ['Poppins', 'Inter', 'Roboto', 'sans-serif'].join(','),
          fontWeight: 600,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
          fontSize: '0.75rem',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
          fontSize: '0.75rem',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
          fontWeight: 500,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontFamily: ['Poppins', 'Inter', 'Roboto', 'sans-serif'].join(','),
            fontWeight: 600,
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-body': {
            fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontFamily: ['Inter', 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
  status: {
    danger: colors.red,
  },
});

function App({ locale, setLocale }) {
  const [loading, setLoading] = useState(true);
  const authenticated = useStore((state) => state.authenticated);
  const setActiveUser = useStore((state) => state.setActiveUser);
  const setAuthenticated = useStore((state) => state.setAuthenticated);

  // Calculate token expiry time for session timeout
  const getTokenExpiry = () => {
    const issuedAt = localStorage.getItem(token_keys.issued_at);
    const expiresIn = localStorage.getItem(token_keys.expiration);

    if (issuedAt && expiresIn) {
      const expiryTime = parseInt(issuedAt) + (parseInt(expiresIn) * 1000);
      return new Date(expiryTime).toISOString();
    }
    return null;
  };

  // Session timeout handlers
  const handleExtendSession = async () => {
    try {
      console.log("Extending session...");
      const response = await refreshAccessToken();
      if (response && response.data && response.data.access_token) {
        const expiresIn = response.data.expires_in || 900;
        const issuedAt = Date.now();
        localStorage.setItem(token_keys.expiration, expiresIn.toString());
        localStorage.setItem(token_keys.issued_at, issuedAt.toString());
        localStorage.setItem(token_keys.access_token, response.data.access_token);
        if (response.data.refresh_token) {
          localStorage.setItem(token_keys.refresh_token, response.data.refresh_token);
        }
        console.log("Session extended successfully - new expiry:", new Date(parseInt(issuedAt) + (parseInt(expiresIn) * 1000)).toISOString());
      } else {
        console.error("Failed to extend session - no access token in response");
        handleLogout();
      }
    } catch (error) {
      console.error("Error extending session:", error);
      // If extension fails, logout user
      handleLogout();
    }
  };

  const handleLogout = () => {
    setAuthenticated(false);
    localStorage.removeItem(token_keys.access_token);
    localStorage.removeItem(token_keys.refresh_token);
    localStorage.removeItem(token_keys.expiration);
    localStorage.removeItem(token_keys.issued_at);
    localStorage.removeItem(token_keys.last_activity);
  };

  // Initialize session timeout
  const { extendSession } = useSessionTimeout(
    handleExtendSession,
    handleLogout
  );

  useEffect(() => {
    const checkAuthentication = async () => {
      const accessToken = localStorage.getItem(token_keys.access_token);
      const refreshToken = localStorage.getItem(token_keys.refresh_token);

      if (!refreshToken) {
        setAuthenticated(false);
        setLoading(false);
        return;
      }

      try {
        let response;
        if (!accessToken) {
          response = await refreshAccessToken();
        } else {
          response = { status: 200 };
        }

        if (response.status === 200) {
          if (!accessToken) {
            console.log("Refresh successful, storing new tokens");
            const expiresIn = response.data.expires_in || 900;
            const issuedAt = Date.now();
            localStorage.setItem(token_keys.expiration, expiresIn.toString());
            localStorage.setItem(token_keys.issued_at, issuedAt.toString());
            localStorage.setItem(token_keys.access_token, response.data.access_token);
            localStorage.setItem(token_keys.refresh_token, response.data.refresh_token);
          }
          const userResponse = await api.getCurrentUser();
          const username = userResponse.data.username;
          setActiveUser(userResponse.data);
          setAuthenticated(true);
        } else {
          setAuthenticated(false);
          localStorage.removeItem(token_keys.access_token);
          localStorage.removeItem(token_keys.refresh_token);
          localStorage.removeItem(token_keys.expiration);
          localStorage.removeItem(token_keys.issued_at);
          localStorage.removeItem(token_keys.last_activity);
        }
      } catch (error) {
        setAuthenticated(false);
        localStorage.removeItem(token_keys.access_token);
        localStorage.removeItem(token_keys.refresh_token);
        localStorage.removeItem(token_keys.expiration);
        localStorage.removeItem(token_keys.issued_at);
        localStorage.removeItem(token_keys.last_activity);
      } finally {
        setLoading(false);
      }
    };

    checkAuthentication();
  }, [setActiveUser, setAuthenticated]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          height: "100vh",
          width: "100vw",
        }}
      >
        <Box sx={{ fontSize: 50, color: colors.main, my: 3, px: 3 }}>
          Prompta.ai
        </Box>
        <Loading />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider maxSnack={3}>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route index element={<LandingPage setAuthenticated={setAuthenticated} />} />
            <Route path={routes.login} element={<Login authentication={setAuthenticated} />} />
            <Route path={routes.register} element={<Register />} />
            <Route path={routes.forgotPassword} element={<ForgotPasswordPage />} />
            <Route path={`${routes.activate}/:token`} element={<ActivatePage />} />
            <Route path={routes.resetPassword} element={<ResetPasswordPage />} />
            <Route path={routes.restoreAccount} element={<RestoreAccountPage />} />
            <Route path={routes.ssoCallback} element={<SsoCallback authentication={setAuthenticated} />} />
            <Route path={`${routes.surveys}/:surveyId/respond/:token`} element={<RespondSurveyPage />} />
            <Route path={`${routes.surveys}/:surveyId/converse/:insightId/:token`} element={<ConverseLinkPage />} />

            {/* Authenticated Routes (Wrapped in Layout) */}
            {authenticated ? (
              <Route element={<Layout
                locale={locale} setLocale={setLocale}
                backgroundColor={"#f5f5f5"}
              />}
              >
                <Route path={routes.home} element={<HomePage locale={locale} setLocale={setLocale} />} />
                <Route path={routes.account}>
                  <Route path="profile" element={<AccountDetailsPage />} />
                  <Route path="subscription" element={<SubscriptionPage />} />
                  <Route path="manage-users" element={<ManageUsersPage />} />
                </Route>

                <Route path={routes.surveys}>
                  <Route path="/surveys" element={<SurveyList />} />
                  <Route path="/surveys/new" element={<SurveyCreate />} />
                  <Route path=":surveyId" element={<SurveyEdit />} />
                </Route>

                {/* Add CollectList route */}
                <Route path={routes.collector}>
                  <Route path=":surveyId" element={<CollectList />} />
                  <Route path=":surveyId/:collectorId/configure" element={<CollectorConfigure />} />
                </Route>

                <Route path={routes.insight}>
                  <Route index element={<InsightPage locale={locale} setLocale={setLocale} />} />
                  <Route path=":surveyId" element={<InsightDetailsPage />} />
                </Route>
                <Route path={routes.documents} element={<DocumentsPage />} />
                <Route path={routes.projects}>
                  <Route index element={<ProjectsPage />} />
                  {/* The project workspace. Was compare/:reportIds — the payload
                      is now fetched by project id, so the URL names the project. */}
                  <Route path=":projectId" element={<CompareResultsPage />} />
                </Route>
                {/* Every retired standalone Impacts URL — the picker, the
                    workspace, Task 3's register and the old Groups screen — now
                    lands on the projects list, because the Register is a tab on
                    a project and picking the project is the way in. */}
                <Route path={`${routes.impacts}/*`} element={<LegacyWorkspaceRedirect />} />
                <Route path="/impact-register/*" element={<LegacyWorkspaceRedirect />} />
                <Route path={`${routes.stakeholderGroups}/*`} element={<LegacyWorkspaceRedirect />} />
                <Route path={routes.help} element={<HelpCenter />} />
              </Route>
            ) : (
              <Route path="*" element={<Navigate to={routes.login} replace />} />
            )}

            {/* Not Found Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>

      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
